/**
 * Where each Azure Storage service client dials, from the connection's own fields (spec §4.1 / §9). ONE resolution
 * for both processes: the main process builds its SDK clients from it (`electron/azure-storage/endpoints.ts`) and the
 * connection form's "resolves to" hint shows its answer, so the hint names the endpoint the connection really uses
 * — an Azurite preset's 127.0.0.1 endpoints, an Advanced override — never a derived `*.core.windows.net` guess.
 *
 * `server` is an account name, a host-style endpoint URL or an Azurite path-style URL (`azureStorageUrl.ts` parses
 * it); `azureStorageEndpoints` overrides are used verbatim; everything else derives from the account name + suffix,
 * or the emulator's sibling ports. Depends on the shared types only, never on `Connection`.
 */
import type { AzureStorageEndpoints, StorageService } from '../../types/azureStorage';
import { serviceOfUrl } from './azureStorageSasUrl';
import { AZURITE_DEFAULT_PORTS, DEFAULT_ENDPOINT_SUFFIX, normalizeStorageAccountInput, serviceEndpointFor } from './azureStorageUrl';

export type StorageEndpointInput = {
  server?: unknown;
  azureStorageEndpoints?: AzureStorageEndpoints;
  azureStorageEmulator?: boolean;
};

export type StorageAccountResolution = {
  accountName?: string;
  /** Explicit overrides merged with the endpoint the `server` URL names. */
  endpoints: AzureStorageEndpoints;
  suffix?: string;
  emulator: boolean;
  pathStyle: boolean;
};

const EMULATOR_SERVICES: ReadonlyArray<keyof typeof AZURITE_DEFAULT_PORTS> = ['blob', 'table', 'queue'];

/** Endpoints Azurite would answer on for a path-style URL: same host, the service's default port. */
function emulatorSiblings(endpoint: string, accountName: string): AzureStorageEndpoints {
  const url = new URL(endpoint);
  const out: AzureStorageEndpoints = {};
  for (const service of EMULATOR_SERVICES) {
    const port = AZURITE_DEFAULT_PORTS[service];
    out[service] = `${url.protocol}//${url.hostname}:${port}/${accountName}`;
  }
  const named = serviceOfUrl(url, true);
  if (named && named !== 'dfs') out[named] = endpoint;
  return out;
}

export function resolveStorageEndpoints(input: StorageEndpointInput): StorageAccountResolution {
  const explicit = input.azureStorageEndpoints ?? {};
  const parsed = normalizeStorageAccountInput(String(input.server ?? ''));
  const emulator = input.azureStorageEmulator === true || parsed.pathStyle;
  if (!parsed.endpoint) {
    return { accountName: parsed.accountName, endpoints: { ...explicit }, emulator, pathStyle: false };
  }
  if (parsed.pathStyle) {
    const siblings = parsed.accountName ? emulatorSiblings(parsed.endpoint, parsed.accountName) : {};
    return { accountName: parsed.accountName, endpoints: { ...siblings, ...explicit }, emulator: true, pathStyle: true };
  }
  const url = new URL(parsed.endpoint);
  const named = serviceOfUrl(url, false);
  const labels = url.hostname.split('.');
  const suffix = parsed.accountName && labels.length > 2 ? labels.slice(2).join('.') : undefined;
  const fromServer: AzureStorageEndpoints = named ? { [named]: parsed.endpoint } : {};
  return { accountName: parsed.accountName, endpoints: { ...fromServer, ...explicit }, suffix, emulator, pathStyle: false };
}

/** The URL a service client is built from, or undefined when nothing can name it. */
export function resolvedServiceEndpoint(input: StorageEndpointInput, service: StorageService | 'dfs'): string | undefined {
  const account = resolveStorageEndpoints(input);
  return serviceEndpointFor(
    { accountName: account.accountName, endpoints: account.endpoints, suffix: account.suffix ?? DEFAULT_ENDPOINT_SUFFIX, emulator: account.emulator },
    service,
  );
}
